import { useCallback, useMemo } from 'react'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useExploreItems, useExploreNearbyItems } from 'tripPlanner/hooks/api/explore'
import type { UseExploreItemsParams, UseExploreNearbyItemsParams } from 'tripPlanner/hooks/api/exploreParams'
import type { ChatExploreCategoryKey } from 'tripPlanner/config/exploreCategories'
import type { ExploreItem } from 'tripPlanner/types/common'
import type { GeoPoint } from 'tripPlanner/types/placeAndDateTime'
import { THINGS_TO_DO_DEFAULT_CATEGORY_KEY } from './thingsToDoCategories'
import { buildStopExploreParams } from 'tripPlanner/utils/exploreStopParams'
import {
  filterPlacesByReviewFloor,
  isNearestPlaceOutsideDestination,
  mergeExplorePlaces,
} from 'tripPlanner/utils/explorePlaces'

// Shapes for the disabled case; the queries short-circuit before either is sent.
const NO_EXPLORE_PARAMS: UseExploreItemsParams = {}
const NO_NEARBY_PARAMS: UseExploreNearbyItemsParams = {}

interface Args {
  /** The stop's formattedAddress when it has one, otherwise its name. */
  destinationQueryText: string
  coordinates: GeoPoint | undefined
  categoryKey: ChatExploreCategoryKey
}

interface ThingsToDoPlacesResult {
  places: Array<ExploreItem>
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  /** The two default-list sources are complementary, not redundant, so a short list is not an error. */
  isPartial: boolean
  isShowingNearest: boolean
  refetch: () => void
}

export default function useThingsToDoPlaces({
  destinationQueryText,
  coordinates,
  categoryKey,
}: Args): ThingsToDoPlacesResult {
  const isDefaultCategory = categoryKey === THINGS_TO_DO_DEFAULT_CATEGORY_KEY

  const params = useMemo(
    () => (coordinates
      ? buildStopExploreParams(categoryKey, { queryText: destinationQueryText, coordinates })
      : undefined),
    [categoryKey, coordinates, destinationQueryText],
  )
  const canSearch = !!params
  const exploreParams = params?.explore ?? NO_EXPLORE_PARAMS
  const nearbyParams = params?.nearby ?? NO_NEARBY_PARAMS

  const nearbyQuery = useExploreNearbyItems(nearbyParams, {
    enabled: canSearch && isDefaultCategory,
  })
  const exploreQuery = useExploreItems(exploreParams, { enabled: canSearch })

  const places = useMemo(() => {
    const merged = isDefaultCategory
      ? mergeExplorePlaces(nearbyQuery.data, exploreQuery.data)
      : exploreQuery.data ?? EmptyArray

    return filterPlacesByReviewFloor(merged)
  }, [exploreQuery.data, isDefaultCategory, nearbyQuery.data])

  // The refetch functions are stable; the query objects are new every render.
  const refetchNearby = nearbyQuery.refetch
  const refetchExplore = exploreQuery.refetch
  // Nearby is scoped to its category: a disabled query keeps the error it had, which would
  // otherwise leave a retry with nothing to re-issue.
  const nearbyFailed = isDefaultCategory && nearbyQuery.isError
  const exploreFailed = exploreQuery.isError
  const refetch = useCallback(() => {
    const retryAll = !nearbyFailed && !exploreFailed
    if (isDefaultCategory && (nearbyFailed || retryAll)) {
      refetchNearby()
    }
    if (exploreFailed || retryAll) {
      refetchExplore()
    }
  }, [exploreFailed, isDefaultCategory, nearbyFailed, refetchExplore, refetchNearby])

  // A disabled query is neither loading nor fetching, so `isInitialLoading` reads false while
  // coordinates resolve; `!canSearch` is what keeps the empty state back until a request exists.
  const isLoading = !canSearch || (isDefaultCategory
    ? nearbyQuery.isInitialLoading || exploreQuery.isInitialLoading
    : exploreQuery.isInitialLoading)

  const isFetching = isDefaultCategory
    ? nearbyQuery.isFetching || exploreQuery.isFetching
    : exploreQuery.isFetching

  const isError = isDefaultCategory
    ? nearbyQuery.isError && exploreQuery.isError
    : exploreQuery.isError

  const isPartial =
    isDefaultCategory && !isError && (nearbyQuery.isError || exploreQuery.isError)

  return {
    places,
    isLoading,
    isFetching,
    isError,
    isPartial,
    isShowingNearest: !isLoading && isNearestPlaceOutsideDestination(places, coordinates),
    refetch,
  }
}
